import {
  Box,
  Card,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
  MenuItem,
  Select,
} from '@mui/material'

import { endOfMonth, format, startOfMonth } from 'date-fns'

import { Notification } from '@components/prereg_validation/notification'
import { useSessionContext } from '@providers/session'
import { i18WithDefault as td, i18WithParams as t } from '@shared/locale'
import { BookingCalendar } from 'components/booking_calendar/booking_calendar'
import { BookingCalendarLegend } from 'components/booking_calendar/booking_calendar_legend'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useClientLocationServiceType } from 'service-api/src/hooks/use_client_location_service_type'
import { ServiceType, SubStatus } from 'service-api/src/types/general'
import { BookingInvoiceRange, useBookingInvoices } from './use_booking_invoices'
import { BookingList } from 'views/booking_list/booking_list'

const ALL = 'all'
type BookingsView = 'list' | 'calendar'

const STATUS_FILTER_OPTIONS: SubStatus[] = [
  SubStatus.BOOKED,
  SubStatus.REQUESTED,
  SubStatus.COMPLETED,
  SubStatus.IN_PROGRESS,
  SubStatus.SCHEDULED,
  SubStatus.CANCELLED,
]

// Retail and package invoices are excluded in all views.
// The API cannot currently exclude multiple substatuses, so we filter on the frontend.
const EXCLUDED_SERVICE_TYPES = new Set<string>([ServiceType.RETAIL, ServiceType.PACKAGES])

const SERVICE_TYPE_FILTER_OPTIONS: ServiceType[] = Object.values(ServiceType).filter(
  (serviceType) => !EXCLUDED_SERVICE_TYPES.has(serviceType)
)

const getStatusLabel = (subStatus: string): string => td(`calendar.status.${subStatus}`) || subStatus
const getServiceTypeLabel = (serviceType: string): string => td(`calendar.serviceType.${serviceType}`) || serviceType

const toRange = (start: Date, end: Date): BookingInvoiceRange => ({
  start: format(start, 'yyyy-MM-dd'),
  end: format(end, 'yyyy-MM-dd'),
})

const Skelly = () => {
  return (
    <Stack data-testid={'loading'} sx={{ width: '100%' }} direction="column">
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
    </Stack>
  )
}

export const Bookings = () => {
  const sessionCtx = useSessionContext()
  const theme = useTheme()
  const isBelowLg = useMediaQuery(theme.breakpoints.down('lg'))
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'))

  const { accountName = '' } = useParams()

  const [searchParams, setSearchParams] = useSearchParams()
  const viewSelection: BookingsView = searchParams.get('view') === 'list' ? 'list' : 'calendar'

  const setViewSelection = (view: BookingsView) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('view', view)
        return next
      },
      { replace: true }
    )
  }
  const [statusFilter, setStatusFilter] = useState<string>(ALL)
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>(ALL)

  const [visibleRange, setVisibleRange] = useState<BookingInvoiceRange>(() =>
    toRange(startOfMonth(new Date()), endOfMonth(new Date()))
  )

  useEffect(() => {
    document.title = t('profile.bookings.title')
  }, [])

  const { data: serviceTypes } = useClientLocationServiceType(accountName)

  const serviceTypeIdByName = useMemo(() => {
    const byName = new Map<string, string>()

    serviceTypes?.results?.forEach((lst) => {
      if (lst.serviceType?.name && lst.serviceType.id) {
        byName.set(lst.serviceType.name, lst.serviceType.id)
      }
    })

    return byName
  }, [serviceTypes?.results])

  const serviceTypeId = useMemo(() => {
    return serviceTypeFilter === ALL ? undefined : serviceTypeIdByName.get(serviceTypeFilter)
  }, [serviceTypeFilter, serviceTypeIdByName])

  const filters = useMemo(
    () => ({
      invoiceSubStatus: statusFilter === ALL ? undefined : [statusFilter as SubStatus],
      'locationServiceType.serviceTypeId': serviceTypeId,
    }),
    [statusFilter, serviceTypeId]
  )

  const { invoices, isLoading, hasMore, loadMore, onSort, onRowsPerPageChange } = useBookingInvoices(accountName, {
    view: viewSelection,
    filters,
    range: visibleRange,
  })

  const invoicesForDisplay = useMemo(
    () => invoices.filter((invoice) => !EXCLUDED_SERVICE_TYPES.has(invoice.serviceTypeName ?? '')),
    [invoices]
  )

  const onVisibleRangeChange = useCallback((start: Date, end: Date) => setVisibleRange(toRange(start, end)), [])

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" px={isBelowMd ? 2 : 0} mb={2}>
        <Typography variant="h2">{t('profile.bookings.title')}</Typography>

        <ToggleButtonGroup
          value={viewSelection}
          exclusive
          size={isBelowMd ? 'small' : 'medium'}
          onChange={(_, nextView: BookingsView | null) => {
            if (nextView) setViewSelection(nextView)
          }}
          aria-label={t('profile.bookings.viewToggle', {
            viewName: viewSelection === 'calendar' ? 'list' : 'calendar',
          })}
        >
          <ToggleButton value="list" aria-label={t('profile.bookings.listView')} sx={{ minWidth: '100px' }}>
            {t('profile.bookings.listView')}
          </ToggleButton>

          <ToggleButton value="calendar" aria-label={t('profile.bookings.calendarView')} sx={{ minWidth: '100px' }}>
            {t('profile.bookings.calendarView')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction={isBelowLg ? 'column' : 'row'} justifyContent="space-between" mb={2} px={isBelowMd ? 2 : 0}>
        <Stack direction="row" gap={2}>
          <Select
            value={statusFilter}
            size="small"
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150, maxWidth: 180 }}
            aria-label={t('calendar.filter.status')}
          >
            <MenuItem value={ALL}>{t('calendar.filter.allStatuses')}</MenuItem>
            {STATUS_FILTER_OPTIONS.map((subStatus) => (
              <MenuItem key={subStatus} value={subStatus}>
                {getStatusLabel(subStatus)}
              </MenuItem>
            ))}
          </Select>

          <Select
            value={serviceTypeFilter}
            size="small"
            onChange={(e) => setServiceTypeFilter(e.target.value)}
            sx={{ minWidth: 150, maxWidth: 180 }}
            aria-label={t('calendar.filter.serviceType')}
          >
            <MenuItem value={ALL}>{t('calendar.filter.allServiceTypes')}</MenuItem>
            {SERVICE_TYPE_FILTER_OPTIONS.map((serviceType) => (
              <MenuItem key={serviceType} value={serviceType}>
                {getServiceTypeLabel(serviceType)}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Box mt={2}>{viewSelection === 'calendar' && <BookingCalendarLegend />}</Box>
      </Stack>

      {viewSelection === 'list' && isLoading && invoices.length === 0 ? (
        <Skelly />
      ) : (
        <Box sx={{ pb: '130px', margin: '0 auto' }}>
          {viewSelection === 'list' ? (
            <BookingList
              accountName={accountName}
              invoices={invoicesForDisplay}
              isLoading={isLoading && invoices.length === 0}
              hasMore={hasMore}
              loadMore={loadMore}
              onSort={onSort}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          ) : (
            <Card>
              <BookingCalendar
                accountName={accountName}
                invoices={invoicesForDisplay}
                onVisibleRangeChange={onVisibleRangeChange}
              />
            </Card>
          )}
        </Box>
      )}
    </Box>
  )
}
